import React, { useEffect } from "react";
import { EVENTS, useIdleTimer } from "react-idle-timer";
import {
  useAuthenticatedUser,
  useLogout,
} from "../../Providers/AuthenticatedUserProvider/AuthenticatedUserProvider";
import {
  getSessionStorageItem,
  Key,
  setSessionStorageItem,
} from "../../Utils/SessionStorage";

interface Props {
  children: React.ReactNode;
}

const IDLE_TIMER = 1000 * 60 * 60 * 2; // 2h
const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "wheel",
  "mousedown",
  "touchstart",
  "touchmove",
] as EVENTS[];

const IdleController = (props: Props) => {
  const { children } = props;
  const [authenticatedUser] = useAuthenticatedUser();
  const logout = useLogout();

  const handleOnIdle = () => {
    logout();
  };

  const checkElapsedTime = () => {
    const last = Number(getSessionStorageItem(Key.LAST_ACTIVE));
    if (!last) return;
    if (Date.now() - last >= IDLE_TIMER) {
      handleOnIdle();
    }
  };

  const { start, pause } = useIdleTimer({
    timeout: IDLE_TIMER,
    onIdle: handleOnIdle,
    startManually: true,
    events: ACTIVITY_EVENTS,
    throttle: 5000, // skriv timestamp max var 5:e sekund, inte på varje event
    onAction: () => {
      setSessionStorageItem(Key.LAST_ACTIVE, String(Date.now()));
    },
  });

  useEffect(() => {
    if (!authenticatedUser.isLoggedIn) {
      pause();
      return undefined;
    }

    checkElapsedTime(); // if page is reloaded
    setSessionStorageItem(Key.LAST_ACTIVE, String(Date.now()));
    start();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkElapsedTime();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", checkElapsedTime);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", checkElapsedTime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser.isLoggedIn]);

  return <>{children}</>;
};

export default IdleController;
